//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helpers
import { capFirstChar } from '../../helpers/utilities';
import {
	dateFormat,
	getDateByTimeZone,
	getTimeZoneOffset,
	timeFormatUS,
	time_zones,
} from '../../helpers/date_helper';
import { is_gen_2 } from '../../helpers/interface_helpers';
import { addonInputTypes } from 'src/helpers/addons/addon_helpers';
import { requestEditDevice, requestEditSharedDevice } from '../Lists/list-helpers';
import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
//Stores
import { user_data } from 'src/stores/account/user';
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
//Types
import type { SystemStylesTypes } from '@Shelly/shelly-design-system';
import type { Device } from '../../types/Device';
import type { ChartConfig, ChartVariantsType, TimeZoneInterface } from '../../types/Chart';

const _format = get(format);

const timeLabelFormats = {
	hour: 'H:i',
	day: 'H:i',
	week: 'D d M',
	month: 'd.M',
	day_splitter: 'd.M',
	year: 'M`Y',
	day_of_week: 'D',
	tariff_day_of_week: 'D H:i',
};

const clickAction = {
	day: 'hour',
	week: 'day',
	month: 'day',
	year: 'month',
};

const rangeIntervalFormat = {
	hour: 'day',
	day: 'month',
	month: 'year',
	year: 'year',
};

export const getTimeLabelFormat = (timeLabelFormat: string) => {
	let timeFormat = timeLabelFormats[timeLabelFormat] || timeLabelFormats.day;
	if (/H:i$/.test(timeFormat) && timeFormatUS()) {
		timeFormat = timeFormat.replace('H', 'h') + ' A';
	}
	return timeFormat;
};

export const getClickAction = (period: string) => {
	return clickAction[period];
};

export const getRangeValue = (rangeInterval: string) => {
	return rangeIntervalFormat[rangeInterval];
};

export const updateDeviceInterfaceData = async (device) => {
	let res;
	if (device.shared) {
		res = await requestEditSharedDevice(device);
	} else {
		res = await requestEditDevice(device);
	}

	return res;
};

export const formatSeriesLabel = (value: string, chartsConfig: ChartConfig) => {
	let formatedName = '';
	const split_name = value.split('_');
	const chartName = chartsConfig.name;
	if (!isNaN(Number(split_name[split_name.length - 1]))) {
		split_name.pop();
		formatedName = _format('chart_legend_' + split_name.join('_'), {
			default: value.replace(/_/g, ' '),
		});
	} else {
		formatedName = _format('chart_legend_' + value, {
			default: value == 'batt_invertor' ? 'battery & invertor' : value.replace(/_/g, ' '),
		});
	}
	return capFirstChar(formatedName);
};

export const getChartSensorName = (chartsConfig: ChartConfig, showAlias = true): string => {
	const id = chartsConfig.deviceID;
	let alias = addonInputTypes?.[chartsConfig.name]?.alias || '';
	let sensorName = '';
	if (Number(chartsConfig.sensorID) >= 100) {
		const deviceSettings = devices_settings.getByRealId(id);
		const component = `${chartsConfig.name}:${chartsConfig.sensorID}`;
		sensorName = deviceSettings?.[component]?.name || '';
		if (
			typeof chartsConfig.deviceChannel === 'string' &&
			deviceSettings?.[chartsConfig.deviceChannel]
		) {
			sensorName = deviceSettings[chartsConfig.deviceChannel]?.name || '';
			alias = addonInputTypes?.[chartsConfig.title]?.alias || '';
		}
	} else if (Number(chartsConfig.sensorID) >= 0) {
		const extNames = get(devices)[id]?.external_sensors_names || {};
		const component = `${chartsConfig.sensorID}_${chartsConfig.name}`;
		const extName = extNames[component];
		if (extName && extName.name !== 'unnamed') {
			sensorName = extName.name;
		}
	}
	if (showAlias && !sensorName && alias && chartsConfig?.sensorID) {
		sensorName += `${alias} (${chartsConfig.sensorID})`;
	}
	return sensorName;
};

export const getSystemStyle = (style: SystemStylesTypes['variants']) => {
	return getComputedStyle(document.documentElement).getPropertyValue(style);
};

// HEX TO RGBA CONVERTOR
const removeHash = (hex) => (hex.charAt(0) === '#' ? hex.slice(1) : hex);

const parseHex = (nakedHex) => {
	const isShort = nakedHex.length === 3 || nakedHex.length === 4;

	const twoDigitHexR = isShort
		? `${nakedHex.slice(0, 1)}${nakedHex.slice(0, 1)}`
		: nakedHex.slice(0, 2);
	const twoDigitHexG = isShort
		? `${nakedHex.slice(1, 2)}${nakedHex.slice(1, 2)}`
		: nakedHex.slice(2, 4);
	const twoDigitHexB = isShort
		? `${nakedHex.slice(2, 3)}${nakedHex.slice(2, 3)}`
		: nakedHex.slice(4, 6);
	const twoDigitHexA =
		(isShort ? `${nakedHex.slice(3, 4)}${nakedHex.slice(3, 4)}` : nakedHex.slice(6, 8)) || 'ff';

	return {
		r: twoDigitHexR,
		g: twoDigitHexG,
		b: twoDigitHexB,
		a: twoDigitHexA,
	};
};

const hexToDecimal = (hex) => parseInt(hex, 16);

const hexesToDecimals = ({ r, g, b, a }) => ({
	r: hexToDecimal(r),
	g: hexToDecimal(g),
	b: hexToDecimal(b),
	a: +(hexToDecimal(a) / 255).toFixed(2),
});

const isNumeric = (n) => !isNaN(parseFloat(n)) && isFinite(n);

const formatRgb = (decimalObject, parameterA) => {
	const { r, g, b, a: parsedA } = decimalObject;
	const a = isNumeric(parameterA) ? parameterA : parsedA;

	return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/**
 * Turns an old-fashioned css hex color value into a rgb color value.
 *
 * If you specify an alpha value, you'll get a rgba() value instead.
 *
 * @param The hex value to convert. ('123456'. '#123456', ''123', '#123')
 * @param An alpha value to apply. (optional) ('0.5', '0.25')
 * @return An rgb or rgba value. ('rgb(11, 22, 33)'. 'rgba(11, 22, 33, 0.5)')
 */
export const hexToRgba = (hex, a) => {
	const hashlessHex = removeHash(hex);
	const hexObject = parseHex(hashlessHex);
	const decimalObject = hexesToDecimals(hexObject);

	return formatRgb(decimalObject, a);
};

export const CHART_VARIANTS_LIST: ChartVariantsType[] = ['line', 'bar', 'areaspline', 'area'];

export const MAPPED_CHART_TYPE_INDEXES = {
	line: 0,
	bar: 1,
	areaspline: 2,
	area: 3,
};

export const getDiviceTimeZoneDate = (device: Device): TimeZoneInterface => {
	const now = new Date(),
		result = { tz: '', date: now, timeZoneOffset: 0, time: '' };
	if (device) {
		const settings = devices_settings.getByRealId(device.id);
		result.tz = is_gen_2(device) ? settings?.sys?.location?.tz : settings?.timezone;
		if (!time_zones.includes(result?.tz)) {
			result.tz = get(user_data).timezone;
		}
		result.date = getDateByTimeZone(result.tz);
		result.time = dateFormat(result.date, timeFormatUS() ? 'h:i A' : 'H:i');
		result.timeZoneOffset = getTimeZoneOffset(result.tz);
	}
	return result;
};

export const createChartTooltip = (context) => {
	// Note on bar chart this is triggered Infinity times. This is not due to the updates
	const { chart, tooltip } = context;
	const chartWrapper = chart.canvas.parentNode;
	const tooltipEl = chartWrapper.querySelector('div.tooltip');

	if (!tooltipEl) return;

	tooltipEl.style.opacity = 0;

	if (tooltip.opacity == 0) {
		// Reset tooltip min height on hide
		chartWrapper.style.setProperty('--min-height', `${0}`);
		tooltipEl.style.minHeight = `${0}px`;
		return;
	}
	while (tooltipEl.firstChild) {
		tooltipEl.firstChild.remove();
	}
	if (tooltip.body) {
		const titleLines = tooltip.title || [],
			bodyLines = tooltip.body.map((b) => b.lines);
		titleLines.forEach((label: string) => {
			const title = document.createElement('div');
			title.className = 'title';
			title.innerHTML = label;
			tooltipEl.appendChild(title);
		});
		bodyLines.forEach((text: string[], b: number) => {
			const content = document.createElement('div');
			content.className = 'content';
			const row = document.createElement('div');
			row.className = 'c-row';
			const colors = tooltip.labelColors[b];
			text.forEach((item: string, i: number) => {
				item.match(/<i(c|b|h)>/g)?.forEach(
					(i) =>
						(item = item.replace(RegExp(i, 'g'), getTooltipIcons(i, !item.startsWith(i)))),
				);
				if (i == 0) {
					const circle = document.createElement('span'),
						label = document.createElement('div');
					circle.className = 'circle';
					circle.style.background = colors.backgroundColor;
					if (item == 'N/A') {
						let color = colors.borderColor;
						if (context.chart.config.type == 'bar') {
							color = colors.backgroundColor;
							circle.style.background = colors.borderColor;
						}
						circle.style.border = `1px solid ${color}`;
					}
					label.className = 'label';
					label.innerHTML = item;
					content.appendChild(circle);
					content.appendChild(row);
					row.appendChild(label);
				} else if (item) {
					const label = document.createElement('div');
					label.className = 'label';
					label.innerHTML = item;
					row.appendChild(label);
				}
			});
			tooltipEl.appendChild(content);
		});
	}

	const canvasClientRect = chart.canvas.getBoundingClientRect();
	const tooltipClientRect = tooltipEl.getBoundingClientRect();
	const verticalSpacer = 4;
	const horizontalSpacer = 8;
	// Available space when tooltip is shown.
	const horizontalFreeSpace = canvasClientRect.width - tooltipClientRect.width + horizontalSpacer;
	// Horizontal boundaries
	const minLeft = chart.chartArea.left;
	const maxLeft = horizontalFreeSpace <= 0 ? horizontalSpacer : horizontalFreeSpace;
	// Center tooltip depend on caret position
	const tooltipLeftCentered = tooltip.caretX - tooltipClientRect.width / 2;
	// Used explicit css selector instead of function for finding closest scrolling parent,
	// because tooltip creation is executed too many times.
	const drawerScrollContainer = tooltipEl.closest('.scroll-drawer');
	const mainScrollContainer = tooltipEl.closest('.main-layout-scroll');
	const chartScrollContainer = tooltipEl.closest('.chart-scroll-container>.scroll-container');

	// Set min height to tooltip content max height, to prevent the constant change in height of the container
	let minHeight = getComputedStyle(chartWrapper).getPropertyValue('--min-height');

	let tooltipTop =
		chart.canvas.offsetTop +
		chart.canvas.getBoundingClientRect().height +
		verticalSpacer -
		chart.legend.height;

	let tooltipLeft = tooltip.caretX;

	// Keep centered to chart line until reach the boundaries
	if (tooltip.caretX >= maxLeft) {
		tooltipLeft = maxLeft;
	} else if (tooltipLeftCentered <= minLeft) {
		tooltipLeft = minLeft;
	} else {
		tooltipLeft = tooltipLeftCentered;
	}

	if (+minHeight < tooltipClientRect.height) {
		minHeight = tooltipClientRect.height;
		chartWrapper.style.setProperty('--min-height', `${minHeight}`);
	}
	tooltipEl.style.minHeight = `${minHeight}px`;

	// Calc chart in drawer available top and bottom space.
	// Show tooltip on top if there is no space below and there is space on top
	if (drawerScrollContainer) {
		const cardSpaceBotton =
			drawerScrollContainer.scrollHeight -
			chartWrapper.offsetParent.offsetTop -
			chartWrapper.offsetParent.clientHeight;
		const offsetBottom = chartWrapper.offsetTop + chartWrapper.offsetHeight;
		const bottomSpaceInParentCard =
			chartWrapper.offsetParent.offsetHeight - offsetBottom - chart.legend.height;

		const isSpaceBotton = bottomSpaceInParentCard + cardSpaceBotton >= tooltipClientRect.height;

		const offsetTop = chartWrapper.offsetTop + chartWrapper.offsetParent.offsetTop;
		const isSpaceTop = offsetTop >= tooltipClientRect.height;

		if (isSpaceTop && !isSpaceBotton) {
			tooltipTop = chart.canvas.offsetTop - tooltipClientRect.height;
		}
	}

	// Calc tooltip available top and bottom space for main scroll container with card grid
	// Show tooltip on top if there is no space below and there is space on top
	const gridContainerWrapper = tooltipEl.closest('.card-grid-wrapper');
	if (mainScrollContainer && gridContainerWrapper) {
		const isMobileDevice = window.innerWidth < SystemScreenBreakpoints.DESKTOP_MIN;
		const mobileMenu = isMobileDevice ? 68 : 0; // On mobile devices (phone or tablet) nav menu is bottom position, and it si with fix height
		const gridContainerStaceTop = gridContainerWrapper.offsetTop; // It can be toolbar, other grid, controls container ...
		const gridListItem =
			chartWrapper.closest('[role="listitem"]') ||
			chartWrapper.offsetParent.parentNode ||
			chartWrapper.offsetParent;

		const spacerPreventStickBottom = 8; // spacer to prevent tooltip to stick to scrollcontainer bottom and show container scrollbars, when not needed;
		const gridCardSpaceBottom =
			mainScrollContainer.scrollHeight -
			gridContainerStaceTop -
			mobileMenu -
			gridListItem.offsetTop -
			gridListItem.clientHeight -
			spacerPreventStickBottom; // Space after grid card in main scroll container

		const offsetBottom = chartWrapper.offsetTop + chartWrapper.offsetHeight;
		const gridCardBottomSpaceAfterChart =
			gridListItem.offsetHeight - offsetBottom + chart.legend.height; // Tooltip can be over the legend

		const isSpaceBottom =
			gridCardBottomSpaceAfterChart + gridCardSpaceBottom >= tooltipClientRect.height;

		const offsetTop = chartWrapper.offsetTop + gridListItem.offsetTop;
		const availableSpaceTop = gridContainerStaceTop + chartWrapper.offsetTop; // Space above the chart wrapper to top of main scroll container
		const isSpaceTop =
			offsetTop >= tooltipClientRect.height || availableSpaceTop >= tooltipClientRect.height;

		if (isSpaceTop && !isSpaceBottom) {
			tooltipTop = chart.canvas.offsetTop - tooltipClientRect.height;
		}
	}

	// Calc tooltip available top and bottom space for scrollable container with set class chart-scroll-container
	// Show tooltip on top if there is no space below and there is space on top
	// Note currently there is no handler for chart legend height and others, because no such usage in the app for this type of container
	const chartFullScreenContainer = tooltipEl.closest('.chart-full-screen-container');
	if (chartScrollContainer && chartFullScreenContainer) {
		const offsetTop = chartWrapper.offsetTop + chartFullScreenContainer.offsetTop;
		const isSpaceTop = offsetTop >= tooltipClientRect.height;

		const offsetBottom = chartWrapper.offsetTop + chartWrapper.offsetHeight;
		const spaceAfterChart =
			chartScrollContainer.scrollHeight - offsetBottom - chartFullScreenContainer.offsetTop;
		const isSpaceBottom = spaceAfterChart >= tooltipClientRect.height;

		if (isSpaceTop && !isSpaceBottom) {
			tooltipTop = chart.canvas.offsetTop - tooltipClientRect.height;
		}
	}

	tooltipEl.style.top = `${tooltipTop}px`;
	tooltipEl.style.left = `${tooltipLeft}px`;
	tooltipEl.style.opacity = 1;
};

const tooltipIcons = {
	'<ic>': '<i class="fas fa-coins text-warning"></i>',
	'<ib>': '<i class="fas fa-bolt text-primary text-color-gradient"></i>',
	'<ih>': '<i class="fas fa-house-day text-success"></i>',
};

const getTooltipIcons = (tag: string, marginLeft = false): string => {
	let icon = tooltipIcons[tag];
	if (marginLeft) icon = icon.replace('class="', 'class="ml ');
	return icon;
};

export const checkForStatisticError = (value: number): boolean => {
	return Math.abs(value) === 999 || Math.abs(value) === Infinity || value === null || isNaN(value);
};

export const deepCloneObj = (obj: any) => {
	let clone = Object.assign({}, obj);
	Object.keys(clone).forEach(
		(key) =>
			(clone[key] =
				typeof obj[key] === 'object' && obj[key] != null ? deepCloneObj(obj[key]) : obj[key]),
	);
	return Array.isArray(obj) ? (clone.length = obj.length) && Array.from(clone) : clone;
};
